import { create } from 'zustand';

export interface CodeItem {
  codeGroupCode: string;
  codeValue: string;
  codeName: string;
  codeOrder: number;
}

export interface SessionData {
  memberId: number;
  loginId: string;
  memberName: string;
  masterYn: string;
  compId: number | null;
  compName: string | null;
  studioId: number | null;
  profileImgUrl: string | null;
  affiliationStatus: 'PENDING' | 'ACTIVE' | 'REJECTED' | 'RELEASED' | null;
}

export interface SubscriptionItem {
  agentSubId: number;
  agentId: number;
  agentName: string;
  agentType: string;
  iconUrl: string | null;
  recommendTitle: string | null;
  recommendDesc: string | null;
  /** TM_AGENT.AGENT_ORDER — LeftNav·스튜디오 홈 메뉴 정렬용. 없으면 프론트 정적 순서로 폴백 */
  agentOrder: number | null;
  createdAt: string;
}

export interface InitData {
  studioId: number | null;
  studioStage: number | null;
  profileImgUrl: string | null;
  subscriptions: SubscriptionItem[];
  codes: CodeItem[];
}

interface AppState {
  session: SessionData | null;
  initLoaded: boolean;
  studioStage: number | null;
  subscriptions: SubscriptionItem[];
  codes: CodeItem[];
  /** LeftNav 현재 실제 폭(px) — rail/expanded × 서브패널 고정 여부로 4가지 값(56/240/280/464).
   *  Drawer "크게보기"(FR-13, viewport - LeftNav 폭)처럼 LeftNav 밖에서 그 폭을 알아야 하는
   *  화면이 구독한다. LeftNav.tsx가 유일한 쓰기 주체. */
  navWidth: number;
  /** TopBar 브레드크럼 임시 덮어쓰기(FR-16) — SiteWorkspaceDrawer처럼 라우트 이동 없이 열리는
   *  오버레이가 열려있는 동안 "나의 현장 › [현장명] › {탭}"을 보여주고, 닫히면 null로 되돌려
   *  라우트 기반 기본 브레드크럼(TopBar.getBreadcrumb)이 다시 쓰이게 한다. */
  breadcrumbOverride: string[] | null;
  // Actions
  setSession: (data: SessionData) => void;
  setInit: (data: InitData) => void;
  setStudioStage: (stage: number) => void;
  setNavWidth: (width: number) => void;
  setBreadcrumbOverride: (crumbs: string[] | null) => void;
  reset: () => void;
  // 코드그룹 코드로 코드 목록 조회
  // ⚠️ useAppStore(s => s.getCodesByGroup('...')) 형태 사용 금지 → 매 렌더마다 새 배열 반환 → 무한루프
  //    안전한 사용: useAppStore.getState().getCodesByGroup('...') 또는 s.codes 직접 선택
  getCodesByGroup: (codeGroupCode: string) => CodeItem[];
}

export const useAppStore = create<AppState>()((set, get) => ({
  session: null,
  initLoaded: false,
  studioStage: null,
  subscriptions: [],
  codes: [],
  navWidth: 240,
  breadcrumbOverride: null,

  setSession: (data) => set({ session: data }),

  setInit: (data) =>
    set({
      initLoaded: true,
      studioStage: data.studioStage,
      subscriptions: data.subscriptions,
      codes: data.codes,
    }),

  setStudioStage: (stage) => set({ studioStage: stage }),

  setNavWidth: (width) => set((s) => (s.navWidth === width ? s : { navWidth: width })),

  setBreadcrumbOverride: (crumbs) => set({ breadcrumbOverride: crumbs }),

  reset: () =>
    set({ session: null, initLoaded: false, studioStage: null, subscriptions: [], codes: [] }),

  getCodesByGroup: (codeGroupCode) =>
    get()
      .codes.filter((c) => c.codeGroupCode === codeGroupCode)
      .sort((a, b) => a.codeOrder - b.codeOrder),
}));
